// An org's Technology & Tools stack (spec 2026-10-15).
//
// Selected tools live in profile_data.technologyTools as `{ category, tools }[]`.
// Older data often lands every tool in one "" or "Other" bucket with only a
// `name`. `normalizeToolStack` is the one reader every surface (the editor
// included) renders through; `serializeToolStack` is the editor's one writer.

import { looksLikePricing } from "@/lib/noPricing";
import { OTHER_TOOL_CATEGORY, TOOL_CATEGORIES, isToolCategorySlug, toolCategoryMeta } from "@/lib/toolCategories";

export interface CatalogEntry {
  slug: string;
  name: string;
  category_slug: string;
  domain: string | null;
  aliases?: string[];
  popular_rank?: number | null;
}

export type ToolAccess = "included" | "discounted" | "agent_paid";
export const TOOL_ACCESS_VALUES: readonly ToolAccess[] = ["included", "discounted", "agent_paid"];
export const TOOL_NOTE_MAX = 140;
export const MAX_DAILY_DRIVERS = 3;

export interface StackTool {
  name: string;
  domain?: string | null;
  categorySlug?: string;
  slug?: string;
  logoUrl?: string;
  // Spec 2026-10-15 §4.1. Written by the editor from PR 2; read here from PR 1.
  inHouse?: boolean;
  driver?: boolean;
  access?: ToolAccess;
  trained?: boolean;
  note?: string;
}

export interface StackCategory {
  category: string;
  tools: StackTool[];
}

const norm = (s: string) => (s || "").trim().toLowerCase();
// Drop parenthetical qualifiers so "Command (Keller Williams)" ~ "Command".
const stripParen = (s: string) =>
  norm(s).replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim();

// Legacy / synonym category names → canonical tool_categories slug. Used when a
// saved bucket keeps an older category label than the current catalog uses.
const LEGACY_CATEGORY_SLUG: Record<string, string> = {
  crm: "crm",
  "crm & lead management": "crm",
  marketing: "marketing-and-content",
  "marketing & content": "marketing-and-content",
  "transaction management": "transaction-management-and-esignature",
  transaction: "transaction-management-and-esignature",
  transactions: "transaction-management-and-esignature",
  "lead generation": "lead-generation",
  "lead gen": "lead-generation",
  "home search": "idx-website",
  "market analysis": "cma-market-analysis",
  communication: "video-and-communication",
  "finance & accounting": "back-office-and-accounting",
  "showing & scheduling": "showing-and-scheduling",
};

export interface CatalogIndex {
  ready: boolean;
  bySlug: Map<string, CatalogEntry>;
  match(name: string): CatalogEntry | null;
}

/** Build a lookup over the catalog by slug, name, alias, and paren-stripped name. */
export function buildCatalogIndex(catalog: CatalogEntry[] | undefined | null): CatalogIndex {
  const bySlug = new Map<string, CatalogEntry>();
  const byName = new Map<string, CatalogEntry>();
  const byNorm = new Map<string, CatalogEntry>();
  for (const t of catalog || []) {
    bySlug.set(t.slug, t);
    if (!byName.has(norm(t.name))) byName.set(norm(t.name), t);
    const sp = stripParen(t.name);
    if (sp && !byNorm.has(sp)) byNorm.set(sp, t);
    for (const a of t.aliases || []) {
      if (!byName.has(norm(a))) byName.set(norm(a), t);
      const asp = stripParen(a);
      if (asp && !byNorm.has(asp)) byNorm.set(asp, t);
    }
  }
  return {
    ready: (catalog?.length || 0) > 0,
    bySlug,
    match(name: string) {
      const n = norm(name);
      return byName.get(n) || byNorm.get(stripParen(name)) || null;
    },
  };
}

function matchFor(tool: StackTool, index: CatalogIndex): CatalogEntry | null {
  return (tool.slug ? index.bySlug.get(tool.slug) : undefined) || index.match(tool.name) || null;
}

/** Stable identity for de-duping: catalog slug when known, else lowercased name. */
export function toolDedupeKey(tool: StackTool, index: CatalogIndex): string {
  const match = matchFor(tool, index);
  if (match) return "slug:" + match.slug;
  if (tool.slug) return "slug:" + tool.slug;
  return "name:" + norm(tool.name);
}

// ── The one reader (spec 2026-10-15 §4.2) ─────────────────────────────────────

export interface NormalizedTool {
  name: string;
  slug: string | null;
  categorySlug: string;
  inHouse: boolean;
  driver: boolean;
  access: ToolAccess | null;
  trained: boolean;
  note: string | null;
}

export interface NormalizedCategory {
  slug: string;
  name: string;
  tools: NormalizedTool[];
}

export interface NormalizedStack {
  /** "Built in-house" block. Never in drivers or categories. */
  inHouse: NormalizedTool[];
  /** Daily drivers, at most MAX_DAILY_DRIVERS, stored order. Never in categories. */
  drivers: NormalizedTool[];
  /** Everything else by library category, non-empty only, library order, Other last. */
  categories: NormalizedCategory[];
  /** Every surviving tool in stored order (the editor's working list from PR 2). */
  all: NormalizedTool[];
  isEmpty: boolean;
}

export const EMPTY_TOOL_STACK: NormalizedStack = Object.freeze({
  inHouse: [],
  drivers: [],
  categories: [],
  all: [],
  isEmpty: true,
}) as NormalizedStack;

export const EMPTY_CATALOG_INDEX: CatalogIndex = buildCatalogIndex([]);

const cleanText = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** A saved bucket label → library slug: a slug as-is, a category name, a legacy label, else null. */
function bucketCategorySlug(label: string): string | null {
  const n = norm(label);
  if (!n) return null;
  if (isToolCategorySlug(n)) return n;
  const byName = TOOL_CATEGORIES.find((c) => norm(c.name) === n);
  if (byName) return byName.slug;
  const legacy = LEGACY_CATEGORY_SLUG[n];
  return legacy && isToolCategorySlug(legacy) ? legacy : null;
}

// Catalog first, then the tool's saved slug, then its old group, then Other.
function resolveCategory(tool: StackTool, bucketLabel: string, match: CatalogEntry | null): string {
  if (match && isToolCategorySlug(match.category_slug)) return match.category_slug;
  if (isToolCategorySlug(tool.categorySlug)) return tool.categorySlug;
  return bucketCategorySlug(bucketLabel) ?? OTHER_TOOL_CATEGORY;
}

/**
 * Read profile_data.technologyTools in any shape that exists (flat "" bucket,
 * admin-named groups, legacy labels, the new slug buckets, malformed values)
 * into the display model every surface renders. Applies the no-pricing rule
 * to every name (slug or not) and note, the in-house description requirement,
 * the driver cap and the in-house/driver exclusion. Never throws.
 */
export function normalizeToolStack(raw: unknown, index: CatalogIndex): NormalizedStack {
  if (!Array.isArray(raw)) return EMPTY_TOOL_STACK;

  const seen = new Set<string>();
  const all: NormalizedTool[] = [];
  let driverCount = 0;

  for (const bucket of raw) {
    if (!bucket || typeof bucket !== "object" || !Array.isArray((bucket as StackCategory).tools)) continue;
    const label = typeof (bucket as StackCategory).category === "string" ? (bucket as StackCategory).category : "";

    for (const tool of (bucket as StackCategory).tools) {
      if (!tool || typeof tool !== "object") continue;
      const name = cleanText(tool.name);
      if (!name || looksLikePricing(name)) continue;

      const inHouse = tool.inHouse === true;
      const match = inHouse ? null : matchFor({ ...tool, name }, index);
      const key = inHouse
        ? "inhouse:" + norm(name)
        : match
          ? "slug:" + match.slug
          : tool.slug
            ? "slug:" + tool.slug
            : "name:" + norm(name);
      if (seen.has(key)) continue;

      const rawNote = cleanText(tool.note);
      const note = rawNote && rawNote.length <= TOOL_NOTE_MAX && !looksLikePricing(rawNote) ? rawNote : null;
      if (inHouse && !note) continue;
      seen.add(key);

      const driver = !inHouse && tool.driver === true && driverCount < MAX_DAILY_DRIVERS;
      if (driver) driverCount++;

      all.push({
        name,
        slug: inHouse ? null : (tool.slug ?? match?.slug ?? null),
        categorySlug: resolveCategory(tool, label, match),
        inHouse,
        driver,
        access: TOOL_ACCESS_VALUES.includes(tool.access as ToolAccess) ? (tool.access as ToolAccess) : null,
        trained: tool.trained === true,
        note,
      });
    }
  }

  if (all.length === 0) return EMPTY_TOOL_STACK;

  const byCategory = new Map<string, NormalizedTool[]>();
  for (const tool of all) {
    if (tool.inHouse || tool.driver) continue;
    const list = byCategory.get(tool.categorySlug) ?? [];
    list.push(tool);
    byCategory.set(tool.categorySlug, list);
  }
  const categories = [...byCategory.entries()]
    .map(([slug, tools]) => ({ slug, name: toolCategoryMeta(slug).name, tools }))
    .sort((a, b) => toolCategoryMeta(a.slug).sortOrder - toolCategoryMeta(b.slug).sortOrder);

  return {
    inHouse: all.filter((t) => t.inHouse),
    drivers: all.filter((t) => t.driver),
    categories,
    all,
    isEmpty: false,
  };
}

// ── The writer, tool key and non-tool check (spec 2026-10-15 §4.1, §6.2) ──────

export const TOOL_NAME_MAX = 60;

/**
 * The dedupe identity of a normalized tool. Pass the catalog index to get
 * exactly the key normalizeToolStack dedupes on: a stored slug the catalog
 * does not know falls back to the name's catalog match there, so without the
 * index two different tools can share a key. The editor always passes it.
 */
export function normalizedToolKey(
  tool: Pick<NormalizedTool, "name" | "slug" | "inHouse">,
  index?: CatalogIndex,
): string {
  if (tool.inHouse) return "inhouse:" + norm(tool.name);
  if (index) return toolDedupeKey({ name: tool.name, slug: tool.slug ?? undefined }, index);
  if (tool.slug) return "slug:" + tool.slug;
  return "name:" + norm(tool.name);
}

/** Write the editor's working list as stored buckets: one per non-empty category, library order, Other last. */
export function serializeToolStack(tools: NormalizedTool[], index: CatalogIndex): StackCategory[] {
  const buckets = new Map<string, StackTool[]>();
  for (const t of tools) {
    const categorySlug = isToolCategorySlug(t.categorySlug) ? t.categorySlug : OTHER_TOOL_CATEGORY;
    const out: StackTool = { name: t.name.trim().slice(0, TOOL_NAME_MAX) };
    if (t.slug) {
      out.slug = t.slug;
      const domain = index.bySlug.get(t.slug)?.domain;
      if (domain != null) out.domain = domain;
    }
    out.categorySlug = categorySlug;
    if (t.inHouse) out.inHouse = true;
    else if (t.driver) out.driver = true;
    if (t.access) out.access = t.access;
    if (t.trained) out.trained = true;
    const note = t.note?.trim().slice(0, TOOL_NOTE_MAX);
    if (note) out.note = note;
    const list = buckets.get(categorySlug) ?? [];
    list.push(out);
    buckets.set(categorySlug, list);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => toolCategoryMeta(a).sortOrder - toolCategoryMeta(b).sortOrder)
    .map(([category, list]) => ({ category, tools: list }));
}

export type NonToolKind = "training" | "leads" | "support";

export interface NonToolHint {
  kind: NonToolKind;
  message: string;
  linkLabel: string;
  chapter: "growth" | "offer" | "operations";
  sectionId: "training" | "leads" | "support";
}

const wordsRe = (words: string[]) =>
  new RegExp("(?<![\\p{L}\\p{N}])(?:" + words.map((w) => w.replace(/[-\s]/g, "[-\\s]")).join("|") + ")(?![\\p{L}\\p{N}])", "iu");

const TRAINING_RE = wordsRe(["train", "training", "trainings", "coach", "coaching", "role play", "roleplay", "mentor", "mentorship", "workshop", "class", "classes", "course", "bootcamp"]);
const SUPPORT_RE = wordsRe(["va", "virtual assistant", "assistant", "assistants", "coordinator", "tc", "videographer", "photographer", "stager", "staging", "service", "services", "concierge"]);
const LEADS_RE = wordsRe(["lead", "leads", "lead source", "referral", "referrals", "sphere", "open house", "open houses", "farming", "ppc"]);

const HINTS: Record<NonToolKind, NonToolHint> = {
  training: { kind: "training", message: "This sounds like training. Add it under Growth › Training.", linkLabel: "Go to Training", chapter: "growth", sectionId: "training" },
  leads: { kind: "leads", message: "This sounds like a lead source. Add it under Offer › Lead sources.", linkLabel: "Go to Lead sources", chapter: "offer", sectionId: "leads" },
  support: { kind: "support", message: "This sounds like a service. Add it under Support & services.", linkLabel: "Go to Support & services", chapter: "operations", sectionId: "support" },
};

/** Spot a custom entry that belongs in another section of the editor (spec §6.2). */
export function classifyNonTool(text: string): NonToolHint | null {
  const t = (text || "").trim();
  if (!t) return null;
  if (TRAINING_RE.test(t)) return HINTS.training;
  if (SUPPORT_RE.test(t)) return HINTS.support;
  if (t.split(",").filter((p) => p.trim()).length >= 2 || LEADS_RE.test(t)) return HINTS.leads;
  return null;
}
